import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { usePostHog } from 'posthog-js/react';

interface BackgroundImage {
  path: string;
  name: string;
  category: string;
}

interface BackgroundPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  currentBackground: string | null;
  onBackgroundSelect: (backgroundPath: string | null) => void;
  anchorPosition?: { top: number; right: number };
}

// Preset productivity-friendly colors
const PRESET_COLORS = [
  { name: 'Sage Green', value: '#8fbc8f' },
  { name: 'Soft Blue', value: '#87ceeb' },
  { name: 'Warm Gray', value: '#d3d3d3' },
  { name: 'Lavender', value: '#e6e6fa' },
  { name: 'Peach', value: '#ffdab9' },
  { name: 'Mint', value: '#f0fff0' },
  { name: 'Rose', value: '#ffe4e1' },
  { name: 'Cream', value: '#fffdd0' },
];

const BackgroundPalette: React.FC<BackgroundPaletteProps> = ({
  isOpen,
  onClose,
  currentBackground,
  onBackgroundSelect,
  anchorPosition = { top: 80, right: 16 }
}) => {
  const posthog = usePostHog();
  const [backgroundImages, setBackgroundImages] = useState<BackgroundImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState('#87ceeb');
  const [isMobile, setIsMobile] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const paletteRef = useRef<HTMLDivElement>(null);

  // Check if current background is a color (starts with #)
  const isColorBackground = currentBackground?.startsWith('#');
  const isNoBackground = !currentBackground;

  // Load background images from manifest
  useEffect(() => {
    const loadBackgroundImages = async () => {
      try {
        const response = await fetch('/background-manifest.json');
        if (response.ok) {
          const manifest = await response.json();
          setBackgroundImages(manifest);
        } else {
          console.warn('Could not load background manifest, using fallback images');
          // Fallback to basic set if manifest fails to load
          setBackgroundImages([
            {
              path: '/backgrounds/nature/nature-mt-fuji.jpg',
              name: 'Mt Fuji',
              category: 'nature'
            }
          ]);
        }
      } catch (error) {
        console.error('Error loading background manifest:', error);
        // Fallback to basic set
        setBackgroundImages([
          {
            path: '/backgrounds/nature/nature-mt-fuji.jpg',
            name: 'Mt Fuji',
            category: 'nature'
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    loadBackgroundImages();
  }, []);

  // Show color picker when no background is selected
  useEffect(() => {
    setShowColorPicker(Boolean(isNoBackground || isColorBackground));
  }, [isNoBackground, isColorBackground]);

  // Track screen size for responsive positioning
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Trigger mount animation
  useEffect(() => {
    if (isOpen) {
      // Slight delay to ensure DOM is ready for transition
      const timer = setTimeout(() => setIsMounted(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsMounted(false);
    }
  }, [isOpen]);

  // Close palette when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (paletteRef.current && !paletteRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleImageSelect = (imagePath: string) => {
    onBackgroundSelect(imagePath);
    
    // Track background change
    posthog?.capture('background_changed', {
      type: 'image',
      image_path: imagePath
    });
  };

  const handleClearBackground = () => {
    onBackgroundSelect(null);
    setShowColorPicker(true);
    
    // Track background cleared
    posthog?.capture('background_changed', {
      type: 'cleared'
    });
  };

  const handlePresetColorSelect = (color: string) => {
    onBackgroundSelect(color);
    
    // Track background change
    posthog?.capture('background_changed', {
      type: 'preset_color',
      color: color
    });
  };

  const handleCustomColorSelect = (color: string) => {
    setCustomColor(color);
    onBackgroundSelect(color);
    
    // Track background change
    posthog?.capture('background_changed', {
      type: 'custom_color',
      color: color
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] sm:pointer-events-none">
      {/* Mobile: Full-screen backdrop */}
      <div 
        className="sm:hidden absolute inset-0 bg-black/50 transition-opacity duration-300"
        style={{ opacity: isMounted ? 1 : 0 }}
        onClick={onClose}
      />
      
      {/* Floating Palette */}
      <div 
        ref={paletteRef}
        className="absolute sm:bg-white sm:dark:bg-dark-card sm:rounded-xl sm:shadow-xl sm:ring-1 sm:ring-black sm:ring-opacity-5 sm:dark:ring-white sm:dark:ring-opacity-10 sm:border sm:border-gray-200 sm:dark:border-gray-700 p-4 sm:w-72 pointer-events-auto
                   max-sm:inset-x-0 max-sm:bottom-0 max-sm:bg-white max-sm:dark:bg-dark-card max-sm:rounded-t-2xl max-sm:border-t max-sm:border-gray-200 max-sm:dark:border-gray-700 max-sm:pb-safe
                   transition-all duration-300 ease-out
                   max-sm:max-h-[80vh] flex flex-col"
        style={{
          ...(isMobile 
            ? { transform: isMounted ? 'translateY(0)' : 'translateY(100%)' }
            : { top: anchorPosition.top, right: anchorPosition.right }
          ),
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary">
            Backgrounds
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-500 dark:text-dark-secondary dark:hover:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors duration-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="overflow-y-auto flex-1 max-sm:-mx-4 max-sm:px-4 sm:overflow-visible">
          {/* Image Grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="aspect-video rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse"
              />
            ))
          ) : (
            backgroundImages.map((image) => (
            <button
              key={image.path}
              onClick={() => handleImageSelect(image.path)}
              className={`group relative aspect-video rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                currentBackground === image.path
                  ? 'border-blue-500 ring-2 ring-blue-500/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500'
              }`}
            >
                             <img
                 src={image.path}
                 alt={image.name}
                 className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
               />
               <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
              {currentBackground === image.path && (
                <div className="absolute top-1 right-1">
                  <div className="w-3 h-3 bg-blue-500 rounded-full ring-2 ring-white" />
                </div>
              )}
            </button>
          )))}
          </div>

          {/* Clear Background Option - TEMPORARILY HIDDEN */}
          {false && (
            <button
              onClick={handleClearBackground}
              className={`w-full flex items-center justify-center px-4 py-2 rounded-lg border transition-colors duration-200 mb-3 ${
                isNoBackground || isColorBackground
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
              }`}
            >
              <span className="text-sm font-medium">Solid Colors</span>
            </button>
          )}

          {/* Color Picker Section - TEMPORARILY HIDDEN */}
          {false && showColorPicker && (
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
              showColorPicker ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                {/* Preset Colors */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Preset Colors
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => handlePresetColorSelect(color.value)}
                        className={`w-12 h-12 rounded-lg border-2 transition-all duration-200 relative ${
                          currentBackground === color.value
                            ? 'border-blue-500 border-3'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      >
                        {currentBackground === color.value && (
                          <div className="absolute top-1 right-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full ring-1 ring-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Color Picker */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Color
                  </h4>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => handleCustomColorSelect(e.target.value)}
                      className="w-12 h-8 rounded border border-gray-200 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.startsWith('#') && value.length <= 7) {
                          setCustomColor(value);
                          if (value.length === 7) {
                            handleCustomColorSelect(value);
                          }
                        }
                      }}
                      placeholder="#87ceeb"
                      className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundPalette; 